import { setLayerApiConfig } from '@agissc/ktag-map';

export interface AppConfig {
  hostnames: string[];
  basename: string;
  sentryEnv: string;
  apis: {
    login: string;
    profile: string;
    auth: string;
    contextSwitch: string;
    layers: string;
    layerConfig: string;
    landownersRemainingRequests: string;
    landowners: string;
    identifyLayer: string;
    identifyLocation: string;
    userMapDefinitionsLocation: string;
    userFavoritesLocation: string;
    layouts: string;
    print: string;
    codeValues: string;
  };
  tracking: {
    appName: string;
    trackingId: string;
    debug: boolean;
  };
  auth: {
    idpUrl: string;
  };
}

const agchServiceUrls = (
  domain: string,
  layersApiUrl: string,
  authDomain: string | null = null,
) => {
  return {
    login: `https://${authDomain ?? domain}/api/v1/token/login`,
    profile: `https://${authDomain ?? domain}/api/v1/profiles/me`,
    auth: `https://${authDomain ?? domain}/api/v1/token`,
    contextSwitch: `https://${
      authDomain ?? domain
    }/de/smartserviceportal/konto/kontext-wechsel`,
    layers: `https://${layersApiUrl}/v1/onlinekarten/layers`,
    layerConfig: `https://${domain}/geoportal/api/v1/mapservices`,
    landownersRemainingRequests: `https://${domain}/geoportal/api/v1/landowners/RemainingRequests`,
    landowners: `https://${domain}/geoportal/api/v1/landowners`,
    identifyLayer: `https://${layersApiUrl}/v1/onlinekarten/layers/`,
    identifyLocation: `https://${layersApiUrl}/v1/onlinekarten/location/identify`,
    userMapDefinitionsLocation: `https://${layersApiUrl}/v1/onlinekarten/mapdefinitions`,
    userFavoritesLocation: `https://${layersApiUrl}/v1/onlinekarten/favorites`,
    layouts: `https://${domain}/geoportal/api/v1/print/layouts`,
    print: `https://${domain}/geoportal/api/v1/print`,
    codeValues: `https://${domain}/geoportal/api/v1/codevalues`,
  };
};

const getBasename = () => {
  const basename = document.documentElement.getAttribute('data-basename');
  return basename ?? '';
};

const Environments: Record<string, AppConfig> = {
  prod: {
    hostnames: ['apps.geo.ag.ch', 'www.ag.ch'],
    basename: getBasename(),
    sentryEnv: 'production',
    apis: agchServiceUrls('www.ag.ch', 'api.geo.ag.ch'),
    tracking: {
      appName: 'ktag-onlinekarten',
      trackingId: 'G-QQ8DTLJQ5D',
      debug: false,
    },
    auth: {
      idpUrl: '',
    },
  },
  canary: {
    hostnames: ['apps.geo.ag.ch', 'www.ag.ch'],
    basename: getBasename(),
    apis: agchServiceUrls('www.ag.ch', 'api.geo.ag.ch'),
    sentryEnv: 'canary',
    tracking: {
      appName: 'ktag-onlinekarten-int',
      trackingId: 'G-HDW8NEEBY3',
      debug: false,
    },
    auth: {
      idpUrl: '',
    },
  },
  quality: {
    hostnames: ['appsq.geo.ag.ch', 'quality.ag.ch'],
    basename: getBasename(),
    sentryEnv: 'quality',
    apis: agchServiceUrls('quality.ag.ch', 'api-stage.geo.ag.ch'),
    tracking: {
      appName: 'ktag-onlinekarten-int',
      trackingId: 'G-HDW8NEEBY3',
      debug: false,
    },
    auth: {
      idpUrl: '',
    },
  },
  preview: {
    hostnames: ['appsq.geo.ag.ch', 'quality.ag.ch'],
    basename: getBasename(),
    sentryEnv: 'preview',
    apis: agchServiceUrls('quality.ag.ch', 'api-stage.geo.ag.ch'),
    tracking: {
      appName: 'ktag-onlinekarten-int',
      trackingId: 'G-HDW8NEEBY3',
      debug: false,
    },
    auth: {
      idpUrl: '',
    },
  },
  dev: {
    hostnames: ['localhost'],
    basename: getBasename(),
    sentryEnv: 'dev',
    apis: agchServiceUrls('quality.ag.ch', 'api-stage.geo.ag.ch', 'test.ag.ch'),
    tracking: {
      appName: 'ktag-onlinekarten-dev',
      trackingId: 'G-N09D0B69V9',
      debug: false,
    },
    auth: {
      idpUrl: '',
    },
  },
};

const checkUrlPattern = (pattern: string) => document.location.pathname.includes(pattern);

const getConfigFromUrlPattern = () => {
  const patterns = {
    canary: Environments.canary,
    'av-preview': Environments.preview,
  };

  return Object.entries(patterns).find(([key]) => checkUrlPattern(key))?.[1];
};

export function getConfigByCurrentEnv() {
  const configFromUrlPattern = getConfigFromUrlPattern();
  if (configFromUrlPattern) {
    return configFromUrlPattern;
  }

  const configEntry = Object.entries(Environments).find(([, config]) =>
    config.hostnames.includes(document.location.hostname),
  );

  const config = configEntry?.[1] ?? Environments.preview;
  if (!config) {
    throw new Error(`No configuration found for ${document.location.origin}`);
  }

  return config;
}

const currentConfig = getConfigByCurrentEnv();
setLayerApiConfig({
  apiUrl: currentConfig.apis.layers,
  tokenUrl: currentConfig.apis.auth,
});

export const isProd = currentConfig === Environments.prod;

export default currentConfig;
